// /admin/status — post incidents and maintenance on the public status page
// (TOM-930, cloud ADR 0453). Create one with its first message, then post
// updates that move it through its states. Every change shows on /status
// within 30 seconds and is audited.

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import {
  COMPONENT_NAMES,
  IMPACT_LABELS,
  INCIDENT_STATE_LABELS,
  formatInstant,
  type ComponentId,
  type IncidentState,
  type IncidentWithUpdates,
} from '../../lib/statusPage';
import { adminApi, adminApiMessage } from '../adminApi';

type Kind = 'incident' | 'maintenance';

const STATES: Record<Kind, IncidentState[]> = {
  incident: ['investigating', 'identified', 'monitoring', 'resolved'],
  maintenance: ['scheduled', 'in_progress', 'completed'],
};
const COMPONENT_IDS = Object.keys(COMPONENT_NAMES) as ComponentId[];

/** A datetime-local value as an ISO instant, or undefined when empty. */
function toIso(local: string): string | undefined {
  return local === '' ? undefined : new Date(local).toISOString();
}

export function AdminStatus() {
  const [incidents, setIncidents] = useState<IncidentWithUpdates[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await adminApi<{ incidents: IncidentWithUpdates[] }>(
        '/v1/admin/status/incidents'
      );
      setIncidents(data.incidents);
    } catch (err) {
      setError(adminApiMessage(err, 'Failed to load incidents.'));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const isOpen = (i: IncidentWithUpdates) =>
    i.kind === 'incident' ? i.state !== 'resolved' : i.state !== 'completed';

  return (
    <section className="admin-section">
      <header className="admin-section-header">
        <h1>Status page</h1>
        <span className="admin-count">
          <Link to="/status">Open /status</Link>
        </span>
      </header>
      <p className="admin-hint">
        Incidents change the public banner and color the days they touch. They never change the
        uptime percentage, which comes from the minute checks.
      </p>

      {error !== null && <div className="admin-auth-banner error">{error}</div>}

      <CreateIncident onCreated={() => void load()} />

      <h2>Incidents</h2>
      {incidents === null && error === null && <p className="admin-loading-row">Loading…</p>}
      {incidents !== null && incidents.length === 0 && (
        <p className="admin-empty">Nothing posted yet.</p>
      )}
      {incidents !== null && incidents.length > 0 && (
        <div className="scroll-x">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Kind</th>
                <th>State</th>
                <th>Components</th>
                <th>Starts</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {incidents.map((i) => (
                <IncidentRow
                  key={i.id}
                  incident={i}
                  active={isOpen(i)}
                  expanded={open === i.id}
                  onToggle={() => setOpen(open === i.id ? null : i.id)}
                  onChanged={() => void load()}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function IncidentRow({
  incident,
  active,
  expanded,
  onToggle,
  onChanged,
}: {
  incident: IncidentWithUpdates;
  active: boolean;
  expanded: boolean;
  onToggle: () => void;
  onChanged: () => void;
}) {
  return (
    <>
      <tr data-testid={`admin-status-row-${incident.id}`}>
        <td>
          <Link to={`/status/incidents/${incident.id}`}>{incident.title}</Link>
        </td>
        <td>{incident.kind === 'maintenance' ? 'Maintenance' : IMPACT_LABELS[incident.impact]}</td>
        <td>
          <span className={`tb-chip${active ? ' tb-chip--warn' : ''}`}>
            {INCIDENT_STATE_LABELS[incident.state]}
          </span>
        </td>
        <td>{incident.components.map((c) => COMPONENT_NAMES[c]).join(', ')}</td>
        <td>{formatInstant(incident.startsAt)}</td>
        <td>
          <button type="button" className="admin-link-button" onClick={onToggle}>
            {expanded ? 'Close' : 'Updates'}
          </button>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={6}>
            <ol className="admin-status-updates">
              {[...incident.updates].reverse().map((u) => (
                <li key={u.id}>
                  <strong>{INCIDENT_STATE_LABELS[u.state]}</strong> · {formatInstant(u.createdAt)}
                  <div>{u.body}</div>
                </li>
              ))}
            </ol>
            <PostUpdate incident={incident} onPosted={onChanged} />
          </td>
        </tr>
      )}
    </>
  );
}

function PostUpdate({
  incident,
  onPosted,
}: {
  incident: IncidentWithUpdates;
  onPosted: () => void;
}) {
  const [state, setState] = useState<IncidentState>(incident.state);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await adminApi(`/v1/admin/status/incidents/${incident.id}/updates`, {
        method: 'POST',
        body: { state, message },
      });
      setMessage('');
      onPosted();
    } catch (err) {
      setError(adminApiMessage(err, 'The update was not posted.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="admin-status-form" onSubmit={(e) => void submit(e)}>
      <div className="admin-field">
        <label htmlFor={`state-${incident.id}`}>New state</label>
        <select
          id={`state-${incident.id}`}
          value={state}
          onChange={(e) => setState(e.target.value as IncidentState)}
        >
          {STATES[incident.kind].map((s) => (
            <option key={s} value={s}>
              {INCIDENT_STATE_LABELS[s]}
            </option>
          ))}
        </select>
      </div>
      <div className="admin-field">
        <label htmlFor={`message-${incident.id}`}>Message (public)</label>
        <textarea
          id={`message-${incident.id}`}
          value={message}
          maxLength={5000}
          rows={3}
          required
          onChange={(e) => setMessage(e.target.value)}
        />
      </div>
      {error !== null && <p className="admin-inline-error">{error}</p>}
      <button type="submit" className="admin-button" disabled={busy || message.trim() === ''}>
        Post update
      </button>
    </form>
  );
}

function CreateIncident({ onCreated }: { onCreated: () => void }) {
  const [kind, setKind] = useState<Kind>('incident');
  const [title, setTitle] = useState('');
  const [impact, setImpact] = useState<'minor' | 'major'>('minor');
  const [components, setComponents] = useState<ComponentId[]>([]);
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setTitle('');
    setComponents([]);
    setStartsAt('');
    setEndsAt('');
    setMessage('');
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await adminApi('/v1/admin/status/incidents', {
        method: 'POST',
        body: {
          kind,
          title,
          impact: kind === 'maintenance' ? 'maintenance' : impact,
          components,
          state: kind === 'maintenance' ? 'scheduled' : 'investigating',
          startsAt: toIso(startsAt),
          ...(kind === 'maintenance' && endsAt !== '' ? { endsAt: toIso(endsAt) } : {}),
          message,
        },
      });
      reset();
      onCreated();
    } catch (err) {
      setError(adminApiMessage(err, 'The incident was not created.'));
    } finally {
      setBusy(false);
    }
  };

  const ready =
    title.trim() !== '' &&
    components.length > 0 &&
    message.trim() !== '' &&
    (kind === 'incident' || startsAt !== '');

  return (
    <form className="admin-status-form admin-info-card" onSubmit={(e) => void submit(e)}>
      <h2>Post an incident or maintenance</h2>
      <div className="admin-field">
        <label htmlFor="status-kind">Kind</label>
        <select id="status-kind" value={kind} onChange={(e) => setKind(e.target.value as Kind)}>
          <option value="incident">Incident (starts as Investigating)</option>
          <option value="maintenance">Maintenance (starts as Scheduled)</option>
        </select>
      </div>
      <div className="admin-field">
        <label htmlFor="status-title">Title (public)</label>
        <input
          id="status-title"
          type="text"
          value={title}
          maxLength={200}
          required
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>
      {kind === 'incident' && (
        <div className="admin-field">
          <label htmlFor="status-impact">Impact</label>
          <select
            id="status-impact"
            value={impact}
            onChange={(e) => setImpact(e.target.value as 'minor' | 'major')}
          >
            <option value="minor">Minor: partial outage</option>
            <option value="major">Major: major outage</option>
          </select>
        </div>
      )}
      <fieldset className="admin-field admin-status-components">
        <legend>Components</legend>
        {COMPONENT_IDS.map((id) => (
          <label key={id}>
            <input
              type="checkbox"
              checked={components.includes(id)}
              onChange={(e) =>
                setComponents(
                  e.target.checked ? [...components, id] : components.filter((c) => c !== id)
                )
              }
            />{' '}
            {COMPONENT_NAMES[id]}
          </label>
        ))}
      </fieldset>
      <div className="admin-field">
        <label htmlFor="status-starts">
          Starts{kind === 'incident' ? ' (empty for now)' : ''}, your local time
        </label>
        <input
          id="status-starts"
          type="datetime-local"
          value={startsAt}
          required={kind === 'maintenance'}
          onChange={(e) => setStartsAt(e.target.value)}
        />
      </div>
      {kind === 'maintenance' && (
        <div className="admin-field">
          <label htmlFor="status-ends">Planned end, your local time</label>
          <input
            id="status-ends"
            type="datetime-local"
            value={endsAt}
            onChange={(e) => setEndsAt(e.target.value)}
          />
        </div>
      )}
      <div className="admin-field">
        <label htmlFor="status-message">First message (public)</label>
        <textarea
          id="status-message"
          value={message}
          maxLength={5000}
          rows={3}
          required
          onChange={(e) => setMessage(e.target.value)}
        />
      </div>
      {error !== null && <p className="admin-inline-error">{error}</p>}
      <button type="submit" className="admin-button" disabled={busy || !ready}>
        Post
      </button>
    </form>
  );
}
